/**
 * MobileWelcome — the "logged-out inside the native mobile app" screen.
 *
 * The desktop Landing is a marketing preview with a wide two-column layout,
 * a sample-data dashboard and a locked-scores rail. That's the right first
 * impression on a browser, but wrong for a user who just tapped the app icon
 * on their phone: they expect a quiet, obvious "sign in" screen with a couple
 * of outcome-focused reassurances.
 *
 * This component is rendered ONLY when isStandalone() is true (i.e. we're
 * running inside the installed PWA or Emergent's native mobile shell). Web
 * visitors continue to see the desktop Landing.
 */
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, TrendingDown, Wallet } from "lucide-react";

const REASSURANCES = [
    { icon: Wallet,       label: "Keep more of your income",   sub: "Safe-to-spend, updated daily." },
    { icon: TrendingDown, label: "Become debt-free earlier",   sub: "One number, one plan, one nudge." },
    { icon: ShieldCheck,  label: "See when you'll be tax-safe", sub: "GST + income tax reserves in one place." },
];

export default function MobileWelcome() {
    return (
        <div
            className="min-h-screen bg-[#0B1F17] text-[#F0EDE5] flex flex-col antialiased"
            style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
            data-testid="mobile-welcome"
        >
            {/* Ambient glow, kept subtle so the sign-in card stays the hero */}
            <div className="pointer-events-none absolute inset-0 opacity-80">
                <div className="absolute -top-32 -left-16 h-[380px] w-[380px] rounded-full bg-[#0E3B2E] blur-3xl opacity-70" />
                <div className="absolute bottom-[-15%] right-[-10%] h-[380px] w-[380px] rounded-full bg-[#B45309]/25 blur-3xl" />
            </div>

            <main className="relative z-10 flex-1 flex flex-col px-6 pt-16">
                {/* Brand mark */}
                <div className="inline-flex items-center gap-3">
                    <span className="grid h-10 w-10 place-items-center rounded-md bg-[#F0EDE5] text-[#0E3B2E] font-display text-sm font-bold">NZ</span>
                    <div className="leading-tight">
                        <div className="text-[13px] font-medium">NZ Taxation Ltd</div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#F0EDE5]/50">ClientOps</div>
                    </div>
                </div>

                {/* Headline */}
                <div className="mt-12 max-w-md">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[#F0EDE5]/50 mb-3">Welcome back</div>
                    <h1 className="font-display text-[34px] leading-[1.05] tracking-tight font-semibold">
                        Your business,<br />in one glance.
                    </h1>
                    <p className="mt-4 text-[14px] leading-relaxed text-[#F0EDE5]/70">
                        Sign in to see today&apos;s safe-to-spend, who owes you, and the one thing worth doing next.
                    </p>
                </div>

                {/* Reassurance list */}
                <ul className="mt-8 space-y-3" data-testid="mobile-welcome-reassurances">
                    {REASSURANCES.map(({ icon: Icon, label, sub }) => (
                        <li key={label} className="flex items-start gap-3 rounded-2xl border border-[#F0EDE5]/10 bg-[#F0EDE5]/[0.04] backdrop-blur-md px-4 py-3">
                            <span className="grid h-9 w-9 place-items-center rounded-full bg-[#0E3B2E] text-[#F0EDE5] shrink-0">
                                <Icon className="h-4 w-4" strokeWidth={2.2} />
                            </span>
                            <div className="min-w-0">
                                <div className="text-[13.5px] font-medium leading-tight">{label}</div>
                                <div className="mt-0.5 text-[11.5px] text-[#F0EDE5]/60 leading-snug">{sub}</div>
                            </div>
                        </li>
                    ))}
                </ul>
            </main>

            {/* Sign-in CTA pinned toward the bottom so the thumb can reach it */}
            <footer className="relative z-10 px-6 pb-10 pt-6">
                <Link
                    to="/login"
                    data-testid="mobile-welcome-signin"
                    className="group flex w-full items-center justify-between rounded-2xl bg-[#F0EDE5] px-5 py-4 text-[#0B1F17] shadow-lg active:scale-[0.99] transition-transform"
                >
                    <span className="font-medium text-[16px]">Sign in</span>
                    <ArrowRight className="h-4.5 w-4.5 transition-transform group-hover:translate-x-0.5" />
                </Link>
                <p className="mt-4 text-center text-[11.5px] text-[#F0EDE5]/45 leading-relaxed">
                    New here? ClientOps is invite-only — ask your accountant at NZ Taxation Ltd for a link.
                </p>
            </footer>
        </div>
    );
}
